import * as IoC from 'typescript-ioc';
import API, { GRAPHQL_AUTH_MODE } from '@aws-amplify/api';
import { GraphQLOptions } from '@aws-amplify/api-graphql';
import { APPSYNC_CONNECTION_CLOSED_MESSAGE, UNAUTHORIZED_EXCEPTION_MESSAGE } from '../../constants/common.constants';
import { AuthAggregateProvider } from '../../domain/auth/implementations/auth-aggregate-provider';
import { CookieAuthAggregate } from '../../domain/auth/implementations/cookie-auth.aggregate';

type VariableType = { meetingId: number }
    | { roomId: number }
    | { assetMetadataId: number }
    | { eventId: number }
    | { meetingConnectionId: number }
    | { identifier: string }
;
export interface IAppsyncSubscription {
    unsubscribe: () => void;
}

const MAX_UNAUTHORIZED_RETRIES = 1;

export const subscribeAppsyncData = async <T>(
    query: string,
    variables: VariableType,
    callback: (data: T) => void,
    retrySubscriptionFn = () => null,
    unauthorizedRetryCount = 0
): Promise<IAppsyncSubscription> => {

    const options: GraphQLOptions = {
        query: query,
        variables: variables
    };

    const authProvider = IoC.Container.get(AuthAggregateProvider).get();
    if (authProvider instanceof CookieAuthAggregate) {
        // Cookie mode: AppSync requires an explicit authToken in connection_init.
        // GET /fusion-auth/fusion-token reads session.fusionToken from the session DB
        // (authenticated via oe_session cookie — same pattern as any REST call).
        // No token generation or signing on the FE; the BE owns the token lifecycle.
        if (!authProvider.isAuthenticated()) return;
        const fusionToken = await authProvider.getAppsyncToken();
        if (!fusionToken) return;
        options.authToken = fusionToken;
        options.authMode = GRAPHQL_AUTH_MODE.AWS_LAMBDA;
    } else {
        // localStorage mode — inject bearer token
        const sessionToken = await authProvider.getSessionToken();
        if (!sessionToken) return;
        options.authToken = sessionToken;
        options.authMode = GRAPHQL_AUTH_MODE.AWS_LAMBDA;
    }

    // Mutable handle — used and returned only in cookie mode; supports
    // unsubscribe reassignment when self-retry creates a new subscription.
    // eslint-disable-next-line no-empty-function
    const handle: IAppsyncSubscription = { unsubscribe: () => {} };

    const subscription = API.graphql(options)
    // @ts-ignore
    .subscribe({
        next: data => callback(data),
        error: (errorData: { error: { errors: [{ message: string; errorType?: string }] }}) => {
            const { error: { errors }} = errorData;

            if (errors.find(el => el.message === APPSYNC_CONNECTION_CLOSED_MESSAGE)) {
                if (authProvider instanceof CookieAuthAggregate) {
                    authProvider.clearAppsyncToken();
                }
                setTimeout(() => {
                    retrySubscriptionFn();
                });
            }

            if (authProvider instanceof CookieAuthAggregate &&
                errors.find(el => el.message?.includes(UNAUTHORIZED_EXCEPTION_MESSAGE)) &&
                unauthorizedRetryCount < MAX_UNAUTHORIZED_RETRIES) {
                // FA token expired — clear cache so getAppsyncToken() re-fetches on retry.
                // Self-retry rather than calling retrySubscriptionFn so subscriptions that
                // don't provide a retry callback are also recovered.
                // unauthorizedRetryCount caps retries at MAX_UNAUTHORIZED_RETRIES to prevent
                // an infinite loop if the BE keeps rejecting the refreshed token.
                authProvider.clearAppsyncToken();
                setTimeout(async () => {
                    const retryHandle = await subscribeAppsyncData(
                        query, variables, callback, retrySubscriptionFn, unauthorizedRetryCount + 1
                    );
                    if (retryHandle) handle.unsubscribe = () => retryHandle.unsubscribe();
                });
            }
        }
    });

    if (authProvider instanceof CookieAuthAggregate) {
        handle.unsubscribe = () => subscription.unsubscribe();
        return handle;
    }
    return subscription;
};
